import * as React from "react";
import { connectPageService } from "../../../../application/domain/base/base-application-service";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { useModal, useMount } from "../../../../utils/custom-hooks";
import { useParams } from "react-router-dom";
import { Loader } from "../../../organisms/common/loading";
import { ServiceProviderTabs } from "../../../molecules/service-provider-tab";
import { OrganizationBasicInfoEditModal } from "../../../organisms/service-provider/modal/organization-basic-info-edit-modal";
import {
  OrganizationBasicInfoService,
  OrganizationBasicInfoServiceProps,
} from "../../../../application/service/service-provider/basic-info-service";
import { fold } from "fp-ts/lib/Option";
import { ServiceProviderR } from "../routes";
import { PrimaryButton, RadioButton } from "@graffer-inc/console-design-kit";
import { OrgWithUsingFeatures } from "../../../../application/domain/organization/org-with-using-features";
import { emptyFunction } from "../../../../utils/function-utils";

export const OrganizationDetailPage: React.FC<
  OrganizationBasicInfoServiceProps
> = ({
  isLoading,
  selectedOrgWithUsingFeatures,
  fetchOrganization,
  onUpdateOrganization,
}) => {
  const showModal = useModal(false);
  const { id } = useParams<{ id: string }>();

  useMount(async () => {
    await fetchOrganization(id);
  });

  return fold<OrgWithUsingFeatures, JSX.Element>(
    () => <div />,
    (orgUsingFeatures) => (
      <ServiceProviderTemplate
        breadcrumbs={[{ to: ServiceProviderR.Home.link({}), title: "Home" }]}
      >
        <div className="has-text-left">
          <div className="columns">
            <div className="column">
              <ServiceProviderTabs orgId={id} />
              <Loader isLoading={isLoading}>
                <nav className="level">
                  <div className="level-left" />
                  <div className="level-right">
                    <p className="level-item">
                      <PrimaryButton
                        text={"基本情報の更新"}
                        onClick={() => {
                          showModal.open();
                        }}
                      />
                    </p>
                  </div>
                </nav>
                <div className="box" style={{ borderRadius: 0 }}>
                  <article className="media">
                    <div className="media-left">
                      <figure className="image is-128x128">
                        <img
                          src={
                            orgUsingFeatures.organization.logoURL === ""
                              ? "https://bulma.io/images/placeholders/128x128.png"
                              : orgUsingFeatures.organization.logoURL
                          }
                          alt="Logo"
                        />
                      </figure>
                    </div>
                    <div className="media-content">
                      <div className="content">
                        <ul style={{ marginTop: 0 }}>
                          <li>
                            地方公共団体コード：
                            {orgUsingFeatures.organization.localGovCode}
                          </li>
                          <li>
                            テナント名：{orgUsingFeatures.organization.name}
                          </li>
                          <li>スラグ：{orgUsingFeatures.organization.slug}</li>
                          <li>
                            公式サイトURL：
                            <a
                              href={
                                orgUsingFeatures.organization.officialWebSiteURL
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {orgUsingFeatures.organization.officialWebSiteURL}
                            </a>
                          </li>
                          <li>
                            サイト名：
                            {orgUsingFeatures.organization.siteName}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </article>
                </div>
                {showModal.isActive && (
                  <OrganizationBasicInfoEditModal
                    isLoading={isLoading}
                    title={"テナント基本情報の更新"}
                    isActive={showModal.isActive}
                    onSubmit={onUpdateOrganization}
                    updateOrg={orgUsingFeatures.organization}
                    onModal={showModal.close}
                    buttonText={"更新する"}
                  />
                )}
              </Loader>
              <div>
                <RadioButton
                  required
                  label="公的個人認証による電子署名の署名検証方法"
                  description="スマート申請において、申請時に公的個人認証(マイナンバーカード)による電子署名をどのように検証するかの設定です。設定を変更するには開発者に依頼する必要があります。"
                  options={[
                    {
                      name: "cybertrustのiTrustを利用した非同期な検証",
                      value: "cybertrust",
                      description:
                        "申請が完了してから非同期で検証されます。電子証明書が失効していても申請できてしまう場合があります。",
                    },
                    {
                      name: "Graffer社内での同期的な検証",
                      value: "internal",
                      description:
                        "申請前に同期的に検証が完了します。電子証明書が失効していると申請できなくなります。この設定を有効にするにはテナントが自治体であり、自治体から署名検証の委託を受けている必要があります。",
                    },
                  ]}
                  value={
                    orgUsingFeatures.useJPKIInternalVerify
                      ? "internal"
                      : "cybertrust"
                  }
                  onChange={emptyFunction}
                />
              </div>
            </div>
          </div>
        </div>
      </ServiceProviderTemplate>
    ),
  )(selectedOrgWithUsingFeatures);
};

export const OrganizationDetailPageContainer = connectPageService(
  OrganizationDetailPage,
  () => new OrganizationBasicInfoService(),
);
